import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

export type CredentialProtocol = "ssh" | "winrm" | "rdp" | "http_basic" | "snmp" | "mysql";

export const PROTOCOL_LABELS: Record<CredentialProtocol, string> = {
  ssh: "SSH",
  winrm: "WinRM (Windows)",
  rdp: "Escritorio remoto",
  http_basic: "HTTP autenticación básica",
  snmp: "SNMP",
  mysql: "MySQL (base de datos)",
};

export const DEFAULT_PORTS: Record<CredentialProtocol, number> = {
  ssh: 22,
  winrm: 5986,
  rdp: 3389,
  http_basic: 443,
  snmp: 161,
  mysql: 3306,
};

export interface Credential {
  id: string;
  name: string;
  description: string | null;
  username: string;
  secret_type: "password" | "private_key";
  protocol: CredentialProtocol;
  port: number | null;
  client: string | null;
  device_type: string | null;
  site: string | null;
  // Por dónde entra AROC, cuando no coincide con dónde está el equipo.
  // Nulo = la dirección del host en Zabbix.
  test_address: string | null;
  zabbix_host_id: string | null;
  zabbix_host_name: string | null;
  last_tested_at: string | null;
  last_tested_ok: boolean | null;
  // 'ok' | 'rechazada' | 'no_concluyente'. El booleano de arriba junta las dos
  // últimas, y esa confusión hizo que dos credenciales válidas de Windows se
  // vieran en rojo por probarse contra el protocolo equivocado.
  last_tested_status: "ok" | "rechazada" | "no_concluyente" | null;
  last_tested_detail: string | null;
  tested_by: string | null;
  created_at: string;
  updated_at: string;
  /** Si algún módulo la referencia. El vínculo es por nombre y sin llave
   *  foránea, así que borrarla dejaría la referencia colgando. */
  en_uso: boolean;
  en_uso_detalle: string | null;
  /** Deshabilitado: sigue en el inventario pero no puede autenticarse. Lo pone
   *  el sistema con su propia acción, no editando la credencial. */
  disabled_at: string | null;
  disabled_by: string | null;
  disabled_reason: string | null;
}

interface CredentialList {
  items: Credential[];
  /** Cuántas cumplen el filtro. `items` es solo la página pedida. */
  total: number;
  limit?: number;
  offset?: number;
}

interface CreateCredentialBody {
  name: string;
  description?: string;
  username: string;
  secret: string;
  secret_type?: "password" | "private_key";
  protocol?: CredentialProtocol;
  port?: number | null;
  client?: string | null;
  device_type?: string | null;
  site?: string | null;
  test_address?: string | null;
  zabbix_host_id?: string | null;
  zabbix_host_name?: string | null;
}

interface UpdateCredentialBody {
  // El nombre es editable: es el identificador con el que el SOAR pide la
  // credencial, y sin poder corregirlo no había forma de que encontrara las
  // que ya estaban cargadas y probadas.
  name?: string;
  description?: string;
  username?: string;
  secret?: string;
  secret_type?: "password" | "private_key";
  protocol?: CredentialProtocol;
  port?: number | null;
  client?: string | null;
  device_type?: string | null;
  site?: string | null;
  test_address?: string | null;
  zabbix_host_id?: string | null;
  zabbix_host_name?: string | null;
}

export interface ZabbixHostOption {
  hostid: string;
  host: string;
  name: string;
  ip: string | null; // IP de la interfaz principal
}

interface ZabbixRawHost {
  hostid: string;
  host: string;
  name: string;
  interfaces?: { ip?: string; main?: string; useip?: string }[];
}

function extractMainIp(raw: ZabbixRawHost): string | null {
  const ifaces = raw.interfaces ?? [];
  const main = ifaces.find((i) => i.main === "1") ?? ifaces[0];
  return main?.ip ?? null;
}

export function useZabbixHostSearch(q: string) {
  return useQuery({
    queryKey: ["zabbix-hosts-search", q],
    queryFn: async ({ signal }) => {
      const data = await api<{ items: ZabbixRawHost[] }>(
        `/api/v1/config/zabbix/search-hosts?q=${encodeURIComponent(q)}&limit=30`,
        { signal },
      );
      const items: ZabbixHostOption[] = data.items.map((h) => ({
        hostid: h.hostid,
        host: h.host,
        name: h.name,
        ip: extractMainIp(h),
      }));
      return { items };
    },
    enabled: q.length >= 2,
    staleTime: 60_000,
  });
}

export function useTestCredentialAnon() {
  return useMutation({
    mutationFn: (body: {
      name: string;
      username: string;
      secret: string;
      ip: string;
      protocol: CredentialProtocol;
      secret_type: "password" | "private_key";
      port?: number | null;
    }) =>
      api<TestResult>("/api/v1/config/credentials/test", {
        method: "POST",
        body,
      }),
  });
}

// ── Inventario: filtros por faceta ────────────────────────────────────────────

/** Valor que agrupa las credenciales a las que todavía no se les puso cliente,
 *  tipo de equipo o sitio. Filtrar por él es filtrar por `IS NULL`. */
export const SIN_CLASIFICAR = "(sin clasificar)";

export type FacetKey = "client" | "device_type" | "protocol" | "test_status" | "site";

export interface CredentialFilters {
  q?: string;
  client?: string[];
  device_type?: string[];
  protocol?: string[];
  test_status?: string[];
  site?: string[];
}

export interface FacetValue {
  value: string;
  count: number;
}

export interface CredentialFacets extends Record<FacetKey, FacetValue[]> {
  total: number;
}

const FACET_KEYS: FacetKey[] = ["client", "device_type", "protocol", "test_status", "site"];

function toQuery(f: CredentialFilters, extra: Record<string, string> = {}) {
  const p = new URLSearchParams();
  if (f.q?.trim()) p.set("q", f.q.trim());
  for (const k of FACET_KEYS) (f[k] ?? []).forEach((v) => p.append(k, v));
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function useCredentials(f: CredentialFilters, limit = 25, offset = 0, enabled = true) {
  return useQuery({
    queryKey: ["credentials", f, limit, offset],
    queryFn: ({ signal }) =>
      api<CredentialList>(
        `/api/v1/config/credentials${toQuery(f, { limit: String(limit), offset: String(offset) })}`,
        { signal },
      ),
    staleTime: 30_000,
    // La pantalla arranca sin pedir filas: con más de cien credenciales,
    // mostrarlas todas de entrada no ayuda a encontrar ninguna.
    enabled,
  });
}

/** Cuántas credenciales quedarían al marcar cada valor de cada faceta. */
export function useCredentialFacets(f: CredentialFilters) {
  return useQuery({
    queryKey: ["credential-facets", f],
    queryFn: ({ signal }) =>
      api<CredentialFacets>(`/api/v1/config/credentials/facets${toQuery(f)}`, { signal }),
    staleTime: 30_000,
  });
}

export interface TestResult {
  ok: boolean;
  status: "ok" | "rechazada" | "no_concluyente";
  detail: string;
  hostname: string | null;
  error: string | null;
  protocol: string | null;
}

export function useTestCredential(id: string) {
  return useMutation({
    mutationFn: (body: { ip: string; protocol?: CredentialProtocol; port?: number | null }) =>
      api<TestResult>(`/api/v1/config/credentials/${id}/test`, {
        method: "POST",
        body,
      }),
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCredentialBody) =>
      api<Credential>("/api/v1/config/credentials", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      // Los conteos de las facetas cambian con cada alta, edición o
      // borrado. Sin esto la barra lateral seguiría diciendo números
      // viejos, que es peor que no tenerlos.
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

export function useUpdateCredential(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCredentialBody) =>
      api<Credential>(`/api/v1/config/credentials/${id}`, {
        method: "PATCH",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      // Los conteos de las facetas cambian con cada alta, edición o
      // borrado. Sin esto la barra lateral seguiría diciendo números
      // viejos, que es peor que no tenerlos.
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

export function useDeshabilitarCredencial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      api<Credential>(`/api/v1/config/credentials/${id}/deshabilitar`, {
        method: "POST",
        body: { motivo },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

export function useHabilitarCredencial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<Credential>(`/api/v1/config/credentials/${id}/habilitar`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    // No acepta forzar: una credencial en uso no se borra, se deshabilita.
    mutationFn: (id: string) =>
      api<void>(`/api/v1/config/credentials/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      // Los conteos de las facetas cambian con cada alta, edición o
      // borrado. Sin esto la barra lateral seguiría diciendo números
      // viejos, que es peor que no tenerlos.
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

// ── Alta de equipos en el monitoreo propio ────────────────────────────────────
//
// Escribe en el Zabbix de ESTA instancia, no en el del cliente. El backend lo
// rechaza si la dirección configurada no es la propia — el token del cliente sí
// puede crear hosts, así que el candado no puede depender de los permisos.

export interface OpcionZabbix {
  id: string;
  nombre: string;
}

export interface AltaEquipoBody {
  nombre: string;
  ip: string;
  grupo_id: string;
  plantilla_ids: string[];
  puerto?: number | null;
  usar_agente: boolean;
  credential_id?: string | null;
}

export interface AltaEquipoResult {
  zabbix_host_id: string;
  nombre: string;
  creado: boolean;
  credencial_vinculada: boolean;
}

export function useGruposZabbix(enabled = true) {
  return useQuery({
    queryKey: ["alta-equipos", "grupos"],
    queryFn: ({ signal }) =>
      api<{ items: OpcionZabbix[] }>("/api/v1/config/alta-equipos/grupos", { signal }),
    staleTime: 300_000,
    enabled,
    retry: false,
  });
}

export function usePlantillasZabbix(q: string, enabled = true) {
  return useQuery({
    queryKey: ["alta-equipos", "plantillas", q],
    queryFn: ({ signal }) =>
      api<{ items: OpcionZabbix[] }>(
        `/api/v1/config/alta-equipos/plantillas${q ? `?q=${encodeURIComponent(q)}` : ""}`,
        { signal },
      ),
    staleTime: 300_000,
    enabled,
    retry: false,
  });
}

export function useAltaEquipo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AltaEquipoBody) =>
      api<AltaEquipoResult>("/api/v1/config/alta-equipos", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

// ── Importar inventario desde Zabbix ──────────────────────────────────────────
//
// Zabbix sabe qué equipos hay, no con qué contraseña se entra. Los importados
// llegan sin credencial: se ven como "sin credencial" hasta que se les pone una.

export interface EquipoPrevio {
  nombre: string;
  ip: string;
  protocolo: string;
  client: string | null;
  device_type: string | null;
  site: string | null;
  grupos: string[];
  ya_existe: boolean;
}

export interface VistaPrevia {
  total_en_zabbix: number;
  nuevos: number;
  ya_existen: number;
  sin_ip: number;
  items: EquipoPrevio[];
}

export interface ResultadoImportacion {
  importados: number;
  omitidos_ya_existian: number;
  omitidos_sin_ip: number;
  nombres: string[];
}

export type OrigenZabbix = "cliente" | "propio";

export function useVistaPreviaImport(origen: OrigenZabbix, enabled = true) {
  return useQuery({
    queryKey: ["importar-zabbix", "vista-previa", origen],
    queryFn: ({ signal }) =>
      api<VistaPrevia>(`/api/v1/config/importar-zabbix/vista-previa?origen=${origen}`, { signal }),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useImportarZabbix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (origen: OrigenZabbix) =>
      api<ResultadoImportacion>(`/api/v1/config/importar-zabbix?origen=${origen}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

// ── Asignar credencial en lote ────────────────────────────────────────────────
//
// El inventario importado trae decenas de equipos que comparten credencial —38
// routers de Holanda, por ejemplo—. Se aplica sobre el filtro que está puesto.

export interface AsignarEnLoteBody {
  username: string;
  secret: string;
  secret_type: "password" | "private_key";
  protocol?: CredentialProtocol | null;
  sobrescribir: boolean;
}

export interface AsignarEnLoteResult {
  afectadas: number;
  omitidas_ya_tenian: number;
  nombres: string[];
}

export function useAsignarEnLote(f: CredentialFilters) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AsignarEnLoteBody) =>
      api<AsignarEnLoteResult>(`/api/v1/config/credentials/asignar-en-lote${toQuery(f)}`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}

// ── Probar en lote ────────────────────────────────────────────────────────────

export interface ProbarEnLoteResult {
  probadas: number;
  ok: number;
  rechazadas: number;
  no_concluyentes: number;
  omitidas: { nombre: string; motivo: string }[];
}

export function useProbarEnLote(f: CredentialFilters) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<ProbarEnLoteResult>(`/api/v1/config/credentials/probar-en-lote${toQuery(f)}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["credentials"] });
      qc.invalidateQueries({ queryKey: ["credential-facets"] });
    },
  });
}
